import { Popover, PopoverContent, PopoverTrigger } from '@/_components/ui'
import { cn } from '@/_lib/utils'
import { useSearch } from '@tanstack/react-router'
import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { WithAuthComponentProps } from '../../_hocs/with-auth'
import { WithAuth } from '../../_hocs/with-auth'
import { DataTableFilter } from '../_components/data-table-filter'
import { DateRangeFilter } from '../_components/date-range-filter'
import { FiltersBar } from '../_components/filters-bar'
import { InlineSearchbox } from '../_components/inline-searchbox'
import { PersonsTable } from '../_components/persons-table'
import { PageLayout } from '../_components/ui/page-layout'
import {
  getActionNeededStatuses,
  getInPipelineStatuses,
  getStatusesForFilter,
  getStatusOptions,
  getTenantActiveStatuses,
} from '../_constants/admission-status'
import { parseStatusParam } from '../_helpers/parse-status-param'
import { useSearchParams } from '../_hooks/use-search-params'
import { useCompanyStore, useFacilityStore } from '../_stores'
import { ApplicationKanban } from './_components/application-kanban'
import type { EViewToggleButtonType } from './_components/application-view-button'
import { ViewToggleButton } from './_components/application-view-button'
import { CompanyFacilityFilter } from './_components/company-facility-filter'
import { ExportTableData } from './_components/export-table-data'
import { FacilityFilter } from './_components/facility-filter'
import type { ApplicationsSearchParams } from './search-defaults'

const Persons = ({ session }: WithAuthComponentProps) => {
  const userType = session.user.userType
  const isStaff = userType === 'staff'
  const mode = isStaff ? 'staff' : ('company' as const)
  const facilityId = useFacilityStore((s) => s.currentFacility?.id)
  const availableFacilities = useFacilityStore((s) => s.availableFacilities)
  const companyId = useCompanyStore((s) => s.company?.id)
  const searchParams = useSearch({ strict: false }) as ApplicationsSearchParams
  const {
    search = '',
    view = 'list' as EViewToggleButtonType,
    status: statusParam = '',
    startDate,
    endDate,
    facilityId: filterFacilityId,
  } = searchParams

  const { setSearchParams } = useSearchParams()
  const [showEmptyColumns, setShowEmptyColumns] = useState(false)

  const status = useMemo(() => {
    if (!statusParam) return undefined
    const parsed = parseStatusParam(statusParam)
    if (!isStaff) {
      return parsed.flatMap((s) => getStatusesForFilter(s, userType))
    }
    return parsed
  }, [statusParam, isStaff, userType])

  const facilityIds = useMemo(
    () => (filterFacilityId ? filterFacilityId.split(',') : []),
    [filterFacilityId],
  )

  const statusOptions = useMemo(() => getStatusOptions(userType), [userType])
  const statusQuickActions = useMemo(
    () =>
      isStaff
        ? [
            { label: 'Selecionar tudo', values: statusOptions.map((o) => o.value) },
            { label: 'Em tramitação', values: getInPipelineStatuses() },
            { label: 'Ações necessárias', values: getActionNeededStatuses() },
          ]
        : [
            { label: 'Selecionar tudo', values: statusOptions.map((o) => o.value) },
            { label: 'Ativos', values: getTenantActiveStatuses() },
          ],
    [statusOptions, isStaff],
  )

  const hasAnyFilter = Boolean(search || statusParam || startDate || endDate || filterFacilityId)

  const handleClearAll = () => {
    setSearchParams({
      search: undefined,
      status: '',
      startDate: undefined,
      endDate: undefined,
      facilityId: undefined,
    })
  }

  const showFacilityFilter = !isStaff && availableFacilities.length > 1

  return (
    <PageLayout
      title="Pacientes"
      fullBleedContent={view === 'kanban'}
      actions={
        <>
          <ViewToggleButton value={view} />
          {isStaff && (
            <ExportTableData
              mode={mode}
              facilityId={facilityId}
              companyId={companyId}
              userType={userType}
              search={search}
              status={status}
              startDate={startDate}
              endDate={endDate}
            />
          )}
        </>
      }
      filters={
        <FiltersBar>
          <div className="flex items-center gap-2">
            <InlineSearchbox
              search={search}
              placeholder="Busque por CPF, nome, telefone, email..."
            />
            {isStaff && <CompanyFacilityFilter />}
            {showFacilityFilter && <FacilityFilter facilities={availableFacilities} />}
            <DataTableFilter
              title="Status"
              filterKey="status"
              placeholder="Pesquise por status"
              options={statusOptions}
              quickActions={statusQuickActions}
            />
            {view === 'kanban' && (
              <Popover>
                <PopoverTrigger className="flex cursor-pointer items-center gap-1 text-sm font-medium text-brand-grayscale-10">
                  Mais
                  <ChevronDownIcon className="h-3.5 w-3.5" />
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-[220px] p-1"
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  <button
                    type="button"
                    onClick={() => setShowEmptyColumns((prev) => !prev)}
                    className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                  >
                    <div
                      className={cn(
                        'flex h-4 w-4 items-center justify-center rounded-sm border',
                        showEmptyColumns
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-muted-foreground/40 [&_svg]:invisible',
                      )}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </div>
                    <span>Mostrar colunas vazias</span>
                  </button>
                </PopoverContent>
              </Popover>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasAnyFilter && (
              <button
                onClick={handleClearAll}
                className="whitespace-nowrap text-xs text-muted-foreground hover:text-foreground mr-1"
              >
                Limpar tudo
              </button>
            )}
            <DateRangeFilter />
          </div>
        </FiltersBar>
      }
    >
      {view === 'list' ? (
        <PersonsTable
          mode={mode}
          facilityId={facilityId}
          companyId={companyId}
          userType={userType}
          showFacilityColumn={isStaff || availableFacilities.length > 1}
          search={search}
          status={status}
          startDate={startDate}
          endDate={endDate}
          filterFacilityIds={facilityIds}
        />
      ) : (
        <ApplicationKanban
          mode={mode}
          facilityId={facilityId}
          companyId={companyId}
          userType={userType}
          canChangeStatus={isStaff}
          search={search}
          status={status}
          startDate={startDate}
          endDate={endDate}
          showEmptyColumns={showEmptyColumns}
          filterFacilityIds={facilityIds}
        />
      )}
    </PageLayout>
  )
}

export default WithAuth(Persons, { allowedFor: ['staff', 'tenant'] })
